import { Select } from "components/shared/form";
import { dateTimeToView } from "helpers/date";
import React, { useCallback, useEffect, useState } from "react";
import { alert } from "services/alert";
import { getOrganizationNames } from "sources/admin";
import {
    applyOrganizationIntegrationClient,
    deleteOrganizationIntegrationClient,
    getOrganizationIntegrationClients,
    OrganizationIntegrationClientListModel,
} from "sources/admin/integrationClients";

interface OrganizationAccessProps {
    clientId: string;
    modal: (params: {}) => void;
}

const OrganizationSelect = Select as React.ComponentType<any>;

/** Manage organizations available to one integration client. */
export default function OrganizationAccess({ clientId, modal }: OrganizationAccessProps) {
    const [items, setItems] = useState<OrganizationIntegrationClientListModel[]>([]);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    const loadItems = useCallback(() => {
        setLoading(true);
        return getOrganizationIntegrationClients({
            preparedFilter: { and: { clientId } },
        }).then((result) => {
            setItems(result?.data || []);
            setLoading(false);
        });
    }, [clientId]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const addOrganization = () => {
        if (!selectedOrganizationId) {
            alert({ type: "error", title: "Ошибка", message: "Выберите организацию." });
            return;
        }

        modal({
            name: "confirm",
            title: "Добавить доступ к организации?",
            text: "Backend автоматически создаст и настроит технического пользователя.",
            buttons: { confirm: "Добавить", cancel: "Отмена" },
            action: () =>
                applyOrganizationIntegrationClient({ organizationId: selectedOrganizationId, clientId }).then(
                    (result) => !!result,
                ),
            onSuccessAction: () => {
                setSelectedOrganizationId(null);
                loadItems();
            },
        });
    };

    const removeOrganization = (item: OrganizationIntegrationClientListModel) => {
        modal({
            name: "confirm",
            title: "Убрать доступ к организации?",
            text: "Интеграционный клиент больше не сможет выполнять запросы от имени этой организации.",
            buttons: { confirm: "Убрать доступ", cancel: "Отмена" },
            action: () => deleteOrganizationIntegrationClient(item.organizationId, clientId).then((result) => !!result),
            onSuccessAction: loadItems,
        });
    };

    return (
        <section className="mt-4 border-top pt-4" aria-labelledby="organization-access-title">
            <h3 className="h5 mb-3" id="organization-access-title">
                Доступные организации
            </h3>
            <div className="row align-items-end mb-3">
                <div className="col-md-8">
                    <OrganizationSelect
                        label="Организация"
                        inputProps={{ name: "organizationId" }}
                        url={getOrganizationNames}
                        propName="name"
                        value={selectedOrganizationId}
                        excludeItems={items.map((item) => item.organizationId)}
                        onChange={({ value }: { value: number | null }) => setSelectedOrganizationId(value)}
                        menuPortalTarget={document.body}
                    />
                </div>
                <div className="col-md-4 mb-3">
                    <button
                        type="button"
                        className="btn btn-sm btn-brand"
                        disabled={!selectedOrganizationId || loading}
                        onClick={addOrganization}
                    >
                        Добавить организацию
                    </button>
                </div>
            </div>
            <div className="table-responsive">
                <table className="table table-sm">
                    <thead className="thead-light">
                        <tr>
                            <th>Организация</th>
                            <th>Дата добавления</th>
                            <th aria-label="Действия" />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={item.organizationId}>
                                <td>{item.organizationName}</td>
                                <td>{dateTimeToView(item.createdAt, "DD.MM.YYYY HH:mm:ss")}</td>
                                <td className="text-right">
                                    <button
                                        type="button"
                                        className="btn btn-xs btn-outline-danger"
                                        onClick={() => removeOrganization(item)}
                                    >
                                        Убрать доступ
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!loading && items.length === 0 && (
                            <tr>
                                <td colSpan={3} className="text-center text-muted">
                                    Доступ к организациям не настроен
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
